import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Pencil, Users, Trash2, FileDown, Upload, Loader2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const empty = { titolo: "", descrizione: "", contenuto: "", attivo: true };

export default function ManualsAdmin() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(null);
  const [assignIds, setAssignIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/manuals").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); api.get("/admin/manuals/users").then(({ data }) => setUsers(data)); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.put(`/admin/manuals/${edit.id}`, edit);
      else await api.post("/admin/manuals", edit);
      toast.success("Manuale salvato");
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare "${m.titolo}"?`)) return;
    try { await api.delete(`/admin/manuals/${m.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleAttivo = async (m) => {
    try { await api.put(`/admin/manuals/${m.id}`, { attivo: !m.attivo }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadPdf = async (m, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/manuals/${m.id}/pdf`, fd); toast.success("PDF caricato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const removePdf = async (m) => { try { await api.delete(`/admin/manuals/${m.id}/pdf`); load(); } catch (e) { toast.error(apiError(e)); } };

  const openAssign = (m) => { setAssign(m); setAssignIds(m.assigned_user_ids || []); };
  const saveAssign = async () => {
    setBusy(true);
    try { await api.put(`/admin/manuals/${assign.id}/assegna`, { user_ids: assignIds }); toast.success("Assegnazioni salvate"); setAssign(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleUser = (id) => setAssignIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="space-y-6" data-testid="manuals-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest">Manuali</h1>
          <p className="mt-1 text-muted-foreground">Crea le guide e attivale per gli utenti selezionati. Di default nessun utente vede i manuali.</p>
        </div>
        <Button onClick={() => setEdit({ ...empty })} data-testid="manual-new" className="gap-1.5 bg-forest hover:bg-forest/90"><Plus className="h-4 w-4" /> Nuovo manuale</Button>
      </div>

      <div className="grid gap-3">
        {items.map((m) => (
          <Card key={m.id} data-testid={`manual-${m.id}`} className="rounded-xl border-border/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sage text-forest"><BookOpen className="h-5 w-5" /></div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-base font-semibold text-forest">{m.titolo}</h3>
                    {m.predefinito && <Badge variant="outline" className="text-[10px]">Predefinito</Badge>}
                    {!m.attivo && <Badge className="bg-muted text-muted-foreground text-[10px]">Disattivo</Badge>}
                  </div>
                  {m.descrizione && <p className="text-sm text-muted-foreground">{m.descrizione}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-forest/60">
                    <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {(m.assigned_user_ids || []).length} utenti abilitati</span>
                    {m.has_pdf && <span className="inline-flex items-center gap-1"><FileDown className="h-3 w-3" /> {m.pdf_filename || "PDF"}</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5"><Switch checked={m.attivo} onCheckedChange={() => toggleAttivo(m)} data-testid={`manual-attivo-${m.id}`} /><span className="text-xs text-muted-foreground">Attivo</span></div>
                <Button size="sm" variant="outline" onClick={() => openAssign(m)} data-testid={`manual-assegna-${m.id}`} className="gap-1.5"><Users className="h-3.5 w-3.5" /> Assegna</Button>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-forest hover:bg-sage">
                  <Upload className="h-3.5 w-3.5" /> PDF
                  <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(m, e.target.files?.[0])} />
                </label>
                {m.has_pdf && <Button size="icon" variant="ghost" onClick={() => removePdf(m)} title="Rimuovi PDF" className="h-8 w-8 text-muted-foreground"><X className="h-4 w-4" /></Button>}
                <Button size="sm" variant="outline" onClick={() => setEdit({ id: m.id, titolo: m.titolo, descrizione: m.descrizione, contenuto: m.contenuto, attivo: m.attivo })} data-testid={`manual-edit-${m.id}`} className="gap-1.5"><Pencil className="h-3.5 w-3.5" /> Modifica</Button>
                {!m.predefinito && <Button size="icon" variant="ghost" onClick={() => remove(m)} className="h-8 w-8 text-destructive" data-testid={`manual-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica manuale" : "Nuovo manuale"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Titolo</Label><Input data-testid="manual-titolo" value={edit.titolo} onChange={(e) => setEdit({ ...edit, titolo: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Descrizione breve</Label><Input data-testid="manual-descrizione" value={edit.descrizione} onChange={(e) => setEdit({ ...edit, descrizione: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Contenuto</Label>
                <Textarea data-testid="manual-contenuto" rows={12} value={edit.contenuto} onChange={(e) => setEdit({ ...edit, contenuto: e.target.value })}
                  placeholder={"Usa:\n# Titolo sezione\n## Sottotitolo\n- punto elenco\n**grassetto**"} className="font-mono text-sm" />
                <p className="text-xs text-muted-foreground">Formattazione: <code># Titolo</code>, <code>## Sottotitolo</code>, <code>- elenco</code>, <code>**grassetto**</code>.</p>
              </div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={edit.attivo} onCheckedChange={(v) => setEdit({ ...edit, attivo: v })} /> Attivo</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={save} disabled={busy} data-testid="manual-save" className="gap-1.5 bg-forest hover:bg-forest/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Assegna "{assign?.titolo}" agli utenti</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Seleziona gli utenti che potranno vedere questo manuale nella loro Guida.</p>
          <div className="max-h-[50vh] space-y-1 overflow-y-auto">
            {users.map((u) => (
              <label key={u.id} data-testid={`assign-user-${u.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border/60 p-2.5 hover:bg-sage/30">
                <Checkbox checked={assignIds.includes(u.id)} onCheckedChange={() => toggleUser(u.id)} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-forest">{u.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{u.email} · {u.role}{u.codice_collab ? ` · ${u.codice_collab}` : ""}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <span className="text-xs text-muted-foreground">{assignIds.length} selezionati</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAssign(null)}>Annulla</Button>
              <Button onClick={saveAssign} disabled={busy} data-testid="assign-save" className="gap-1.5 bg-forest hover:bg-forest/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva assegnazioni</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
